'use client';
import IconMap from '@/assets/icons/order/icon-map.svg';
import IconList from '@/assets/icons/order/icon-menu-map.svg';
import IconMyPoint from '@/assets/icons/order/icon-my-square.svg';
import IconSearch from '@/assets/icons/order/icon-search.svg';
import { CustomRadioButton } from '@/components';
import RegMapComponent, { CoordinateWithId } from '@/components/shared/regMap';
import { WarehousesQuery } from '@/graphql';
import { Address } from '@/graphql/graphql';
import { getDistance } from '@/helpers/geo';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { toast } from 'react-toastify';
import { Button } from 'rsuite';
import styles from './pickup.module.scss';

type Props = {
  loading: boolean;
  onCheckedChange: (checked: boolean) => void;
  onClickPaymentsTab: () => void;
  orderData: WarehousesQuery | undefined;
  selectedPickupPoint: string | null;
  setSelectedPickupPoint: (item: string | null) => void;
  setAddressFormData: (item: Address | null) => void;
  onSearchChange: (searchTerm: string) => void;
};

const PickupSection = (props: Props) => {
  const [tab, setTab] = useState('list');
  const warehousesData = props?.orderData;
  const [btnState, setBtnState] = useState(true);
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const [isLocating, setIsLocating] = useState(false);
  const t = useTranslations('Components.pickup');
  const tomorrowDate = useMemo(() => {
    return dayjs().add(1, 'day').format('D MMMM');
  }, []);

  const handleSelectedTab = (tab: string) => {
    setTab(tab);
    props.setSelectedPickupPoint(null);
    props.onCheckedChange(true);
  };

  const handleSelectedPickupPoint = useCallback(
    (data: any) => {
      props.setSelectedPickupPoint(data?.node?.id);
      if (data?.node?.store?.address) {
        props.setAddressFormData(data.node.store.address);
      }
      props.onCheckedChange(false);
      setBtnState(false);
    },
    [
      props.setSelectedPickupPoint,
      props.setAddressFormData,
      props.onCheckedChange,
    ]
  );

  useEffect(() => {
    if (props.selectedPickupPoint) {
      setBtnState(false);
    }
  }, [props.selectedPickupPoint]);

  const handlePaymentsTabClick = async () => {
    props.onClickPaymentsTab();
  };

  const getWorkingHours = (workSchedule: any[]) => {
    if (!workSchedule) {
      return 'Уточняйте график работы';
    }

    const allOpen = workSchedule.every(item => item.showButton);
    const openTime = workSchedule[0]?.openTime;
    const closeTime = workSchedule[0]?.closeTime;

    if (allOpen && openTime && closeTime) {
      return `${openTime} – ${closeTime}, ${t('without-weekends')}`;
    } else {
      const closedDay = workSchedule.find(item => !item.showButton);
      const day = closedDay?.day;
      if (openTime && closeTime && day) {
        return `${openTime} – ${closeTime}, ${declensionDay(day)} выходной`;
      }
      return 'Уточняйте график работы';
    }
  };

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      props.onSearchChange(localSearchTerm);
    }, 500);
    return () => {
      clearTimeout(debounceTimer);
    };
  }, [localSearchTerm, props.onSearchChange]);

  const declensionDay = (day: string) => {
    switch (day) {
      case 'Monday':
        return t('days.monday');
      case 'Tuesday':
        return t('days.tuesday');
      case 'Wednesday':
        return t('days.wednesday');
      case 'Thursday':
        return t('days.thursday');
      case 'Friday':
        return t('days.friday');
      case 'Saturday':
        return t('days.saturday');
      case 'Sunday':
        return t('days.sunday');
      default:
        return t('days.unknown');
    }
  };

  const handleNearMeClick = () => {
    if (!navigator.geolocation) {
      toast.error('Геолокация не поддерживается вашим браузером.');
      return;
    }
    if (
      !warehousesData?.warehouses?.edges ||
      warehousesData.warehouses.edges.length === 0
    ) {
      toast.error('Нет доступных пунктов самовывоза для поиска.');
      return;
    }

    setIsLocating(true);

    navigator.geolocation.getCurrentPosition(
      position => {
        const userLat = position.coords.latitude;
        const userLon = position.coords.longitude;

        let closestWarehouse: any = null;
        let minDistance = Infinity;

        warehousesData?.warehouses?.edges.forEach(loc => {
          const warehouseLat = loc?.node?.store?.latitude;
          const warehouseLon = loc?.node?.store?.longitude;
          if (
            typeof warehouseLat === 'number' &&
            typeof warehouseLon === 'number'
          ) {
            const distance = getDistance(
              userLat,
              userLon,
              warehouseLat,
              warehouseLon
            );
            if (distance < minDistance) {
              minDistance = distance;
              closestWarehouse = loc;
            }
          } else {
            toast.error('Склад ${loc?.node?.id} имеет неверные координаты.');
          }
        });

        setIsLocating(false);

        if (closestWarehouse) {
          handleSelectedPickupPoint(closestWarehouse);
        } else {
          toast.error(
            'Не удалось найти пункты самовывоза с корректными координатами поблизости.'
          );
        }
      },
      error => {
        setIsLocating(false);
        toast.error(`Ошибка получения геолокации:, ${error}`);
        let message = 'Не удалось определить ваше местоположение.';
        if (error.code === error.PERMISSION_DENIED) {
          message =
            'Пожалуйста, разрешите доступ к геолокации в настройках браузера.';
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          message = 'Информация о местоположении недоступна.';
        } else if (error.code === error.TIMEOUT) {
          message = 'Превышено время ожидания запроса геолокации.';
        }
        toast.error(`${message}`);
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  const handleMapPointSelect = useCallback(
    (selectedId: string) => {
      const selectedLoc = warehousesData?.warehouses?.edges.find(
        loc => loc?.node?.id === selectedId
      );

      if (selectedLoc) {
        handleSelectedPickupPoint(selectedLoc);
      } else {
        toast.error('Не удалось найти данные для выбранной точки.');
      }
    },
    [warehousesData, handleSelectedPickupPoint]
  );

  const mapCoordinates = useMemo((): CoordinateWithId[] => {
    return (
      warehousesData?.warehouses?.edges
        .map(store => {
          const node = store?.node;
          if (
            !node ||
            !node.id ||
            node.store?.latitude == null ||
            node.store?.longitude == null
          ) {
            return null;
          }
          return {
            id: node.id,
            latitude: node.store.latitude,
            longitude: node.store.longitude,
            title: node.store?.landmark,
            address: node.store?.address?.streetAddress,
            workDays: getWorkingHours(node.store?.workSchedule),
            // phone: node?.store?.phone,
          };
        })
        .filter((coord): coord is CoordinateWithId => coord !== null) ?? []
    );
  }, [warehousesData]);

  return (
    <>
      <div className={styles.pickupWrapper}>
        <div className={styles.header}>
          <h2>{t('choose-point')}</h2>
          <div className={styles.tabs}>
            <Button
              type="button"
              onClick={() => handleSelectedTab('list')}
              className={`${styles.tab} ${tab === 'list' && styles.activeTab}`}
            >
              {' '}
              <IconList />
              {t('list')}
            </Button>
            <Button
              type="button"
              onClick={() => handleSelectedTab('map')}
              className={`${styles.tab} ${tab === 'map' && styles.activeTab}`}
            >
              {' '}
              <IconMap />
              {t('map')}
            </Button>
          </div>
        </div>
        {tab === 'list' && (
          <div className={styles.list}>
            {props.loading
              ? Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className={styles.listItem}>
                    <div className={styles.texts}>
                      <label className={styles.skeletonRadio}>
                        <input
                          type="radio"
                          disabled
                          style={{ pointerEvents: 'none' }}
                        />
                        <h3 className={styles.pointTitle}>
                          <Skeleton className={styles.skeleton} width={240} />
                        </h3>
                      </label>
                      <span>
                        <Skeleton className={styles.skeleton} width={220} />
                      </span>
                      <div className={styles.timeInfo}>
                        <span>
                          <Skeleton className={styles.skeleton} width={180} />
                        </span>
                        <span>
                          <Skeleton className={styles.skeleton} width={240} />
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              : warehousesData?.warehouses?.edges.map((loc, index) => {
                  return (
                    <div key={index} className={styles.listItem}>
                      <CustomRadioButton
                        value={loc?.node?.id}
                        checked={props.selectedPickupPoint === loc?.node?.id}
                        onChange={() => handleSelectedPickupPoint(loc)}
                      >
                        <div className={styles.texts}>
                          <h3 className={styles.pointTitle}>
                            {loc?.node?.store?.address?.name}{' '}
                            <span>• {loc?.node?.store?.landmark}</span>
                          </h3>
                          <div className={styles.timeInfo}>
                            <span>
                              {getWorkingHours(loc?.node?.store?.workSchedule)}
                            </span>
                            <span>{t('storage-time')}</span>
                          </div>
                          <p className={styles.subTitle}>
                            {t('pickup-time', { tomorrowDate })}
                          </p>
                        </div>
                      </CustomRadioButton>
                    </div>
                  );
                })}
          </div>
        )}
        {tab === 'map' && (
          <div className={styles.mapWrapper}>
            <div className={styles.yandexMap}>
              {/* <YandexMap /> */}
              <RegMapComponent
                coordinates={mapCoordinates}
                reg={true}
                height={500}
                zoom={8}
                selectedId={props.selectedPickupPoint}
                onPointSelect={handleMapPointSelect}
              />
            </div>
            <div className={styles.addressPoints}>
              <Button
                className={styles.myBtn}
                type="button"
                onClick={handleNearMeClick}
                disabled={isLocating || props.loading}
              >
                <IconMyPoint />
                {isLocating ? t('definition') : t('next-to-me')}
              </Button>
              <div className={styles.inputSearch}>
                <input
                  type="text"
                  placeholder={t('enter-address')}
                  value={localSearchTerm}
                  onChange={e => setLocalSearchTerm(e.target.value)}
                />
                <IconSearch />
              </div>
              <div className={styles.addressList}>
                {warehousesData?.warehouses?.edges.map((loc, index) => {
                  return (
                    <div key={index} className={styles.listItem}>
                      <CustomRadioButton
                        value={loc?.node?.id}
                        checked={props.selectedPickupPoint === loc?.node?.id}
                        onChange={() => handleSelectedPickupPoint(loc)}
                      >
                        <div className={styles.texts}>
                          <h3 className={styles.pointTitle}>
                            {loc?.node?.store?.address?.name}{' '}
                            <span>• {loc?.node?.store?.landmark}</span>
                          </h3>
                          <div className={styles.timeInfo}>
                            <span>
                              {getWorkingHours(loc?.node?.store?.workSchedule)}
                            </span>
                            <span>{t('storage-time')}</span>
                          </div>
                        </div>
                      </CustomRadioButton>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
      <div className={styles.bottom}>
        <Button
          type="button"
          disabled={btnState}
          onClick={handlePaymentsTabClick}
          className={btnState ? styles.disabledBtn : styles.paymentBtn}
        >
          {t('switch-to-payment')}
        </Button>
      </div>
    </>
  );
};

export default PickupSection;
